import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import TextComponent from 'Component/Render/Text';
import Game from 'Game';
import { RewardType } from 'Utility/Rewards/RewardType';
import { IAPReward } from 'Utility/RewardHelper';
import { ProductId } from 'Utility/Product';
import Point from 'Utility/Point';
import { ShowPlinkoReward } from 'System/Animation/Clip/ShowPlinkoReward';
import Armature from 'Prototype/Armature';
import BaseUIHandler from 'System/UI/BaseUIHandler';
import { PopupID } from 'State/UI/PopupID';
import BaseUISystem from 'System/UI/BaseUISystem';
import { IGameState } from 'State/Game/IGameState';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { PlinkoMinigameLayout } from 'Layouts/Plinko/PlinkoMinigameLayout';
import { getPlinkoConfig } from '../Helpers';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { createSelector, OutputSelector } from 'reselect';
import { PlinkoActions } from 'State/Plinko/PlinkoActions';
import {
    getRemainingPurchasablePlinkoTokens,
    isPlinkoRewardAnimationCompleted,
    isPlinkoRewardClaimable,
} from 'State/Plinko/PlinkoStateHelper';
import Renderable from 'Component/Renderable';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import Interactable from 'Component/Interactable';
import PlinkoToken from 'Prefabs/Plinko/PlinkoToken';
import DragonbonesComponent from 'Component/Dragonbones';
import { DropPlinkoToken, getNextDropAnimation } from 'System/Animation/Clip/DropPlinkoToken';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import LevelHelper from 'Utility/LevelHelper';
import { IPlinkoConfig } from '../IPlinkoConfig';
import { MathHelper } from 'Utility/MathHelper';
import dragonBones from 'pixi-dragonbones';
import CoinBar from 'Prefabs/UI/HUD/CoinBar';
import { PlinkoRewardCoins } from 'System/Animation/Clip/PlinkoRewardCoins';
import PlinkoSystem from '../PlinkoSystem';
import { scaleCoinRewardWithLevelCost } from 'State/Player/PlayerStateHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { OverlayFadeIn, OverlayFadeOut } from 'System/Animation/Clip';
import Overlay from 'Prototype/UI/Overlay';
import { GameActions } from 'State/Game/GameActions';
import AnimationConfig = dragonBones.AnimationConfig;
import AnimationFadeOutMode = dragonBones.AnimationFadeOutMode;
import { isShopSupported } from 'Utility/Platform';
import { report } from 'report';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';

import getEvent = EventStateHelper.getEvent;
import getEventLayout = EventStateHelper.getEventLayout;

type FarmMapProps = {
    system: BaseUISystem;
};

export default class PlinkoMiniGameHandler extends BaseUIHandler {
    private layout: GLayout<PlinkoMinigameLayout>;

    private coinBar: CoinBar;

    private tokenReward: IAPReward;
    private extraTokenReward: IAPReward;

    private plinkoToken: PlinkoToken;

    private plinkoConfig: IPlinkoConfig;

    // Button selectors
    private dropButtonSelector: OutputSelector<
        [(state: IGameState) => void],
        void,
        (isDropTokenAvailable: boolean, miniGameActive: boolean, isRewardClaimable: boolean) => void
    >;
    private buyButtonSelector: OutputSelector<
        [(state: IGameState) => void],
        void,
        (rewardAnimationCompleted: boolean, shouldShowButton: boolean) => void
    >;
    private claimButtonSelector: OutputSelector<
        [(state: IGameState) => void],
        void,
        (isDropTokenAvailable: boolean, miniGameActive: boolean) => void
    >;
    private closeButtonSelector: OutputSelector<
        [(state: IGameState) => void],
        void,
        (isDropTokenAvailable: boolean, miniGameActive: boolean, isDropping: boolean) => void
    >;
    private remainingPurchasableTokensSelector: OutputSelector<
        [(state: IGameState) => void],
        any,
        (remainingPurchasableTokens: number, availableTokens: number, IAPSupport: boolean) => any
    >;

    private isDropTokenAvailableSelector: OutputSelector<
        [(state: IGameState) => void],
        boolean,
        (res: boolean) => boolean
    > = createSelector(
        (state: IGameState) => InventoryStateHelper.hasInInventory(state, RewardType.PLINKO_TOKEN),
        (isDropTokenAvailable: boolean) => isDropTokenAvailable
    );

    private isMinigameActiveSelector: OutputSelector<
        [(state: IGameState) => void],
        boolean,
        (res: boolean) => boolean
    > = createSelector(
        // TODO: Introduce enum state to clearly define distinct states of the plinko minigame e.g. Active, NoTokens, RewardClaimable, etc.
        (state: IGameState) => typeof state.plinkoState.rewardIndex === 'number',
        isMinigameActive => {
            const state = Game.store.getState();

            const rewardIndex = state.plinkoState.rewardIndex;
            const startIndex = state.plinkoState.startIndex;
            const path = state.plinkoState.path;
            const dropAnimation = state.plinkoState.dropAnimation;

            const chosenReward = this.layout.rewardContainer.children[rewardIndex];

            if (isMinigameActive) {
                StartAnimationClip(
                    new DropPlinkoToken(this.plinkoToken, {
                        dropperArmature: this.layout.dropperArmature as Armature,
                        plinkoToken: this.plinkoToken,
                        tokenContainer: this.layout.tokenContainer,
                        dropAnimation,
                        path,
                        startIndex,
                    }).on(AnimationClipEvent.COMPLETE, () => {
                        const {
                            shadowArmature,
                            rewardContainer,
                            jackpotArmature,
                            rewardArmature,
                            jackpotValue,
                        } = this.layout;

                        const levelEntry = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted(state));
                        const reward = this.plinkoConfig.rewards[rewardIndex];
                        const rewardCoinValue = reward.data.value * levelEntry;

                        StartAnimationClip(
                            new ShowPlinkoReward(chosenReward, {
                                shadowArmature: shadowArmature as Armature,
                                rewardArmature: rewardArmature as Armature,
                                jackpotArmature: jackpotArmature as Armature,
                                jackpot: reward.data.jackpot ?? false,
                                jackpotValue,
                                rewardIndex,
                                rewardChildren: rewardContainer.children,
                                rewardCoinValue: rewardCoinValue,
                            }).on(AnimationClipEvent.COMPLETE, () => {
                                PlinkoActions.setRewardClaimable();
                            })
                        );
                    })
                );
            }
            return isMinigameActive;
        }
    );

    private craneDropperSelector: OutputSelector<
        [(state: IGameState) => void],
        void,
        (isDropTokenAvailable: boolean, miniGameActive: boolean) => void
    >;

    constructor(props: FarmMapProps) {
        super(PopupID.PLINKO_MINI_GAME, props.system, false);
        this.alwaysVisible = true;

        this.plinkoConfig = getPlinkoConfig();
    }

    public getLayout(): GLayout<PlinkoMinigameLayout> {
        return this.layout;
    }

    public onCreate(): void {
        super.onCreate();
        const state = Game.store.getState();

        this.tokenReward = IAPHelper.CACHED_IAP_REWARDS_MAP()[ProductId.PLINKO_TOKEN];
        this.extraTokenReward = IAPHelper.CACHED_IAP_REWARDS_MAP()[ProductId.PLINKO_TOKENS_2_99];

        this.layout = getEventLayout(state, PopupID.PLINKO_MINI_GAME) ?? getLayout(PlinkoMinigameLayout);
        this.system.scene.addGameObject(this.layout.root);

        const {
            dropButton,
            buyButton,
            buyExtraButton,
            claimButton,
            closeButton,
            buyButtonText,
            buyExtraButtonText,
            dropperArmature,
            dropTokenLeftText,
            rewardContainer,
            coinBarContainer,
            charSprite,
            iapInfo,
        } = this.layout;

        this.coinBar = GameObject.Instantiate(CoinBar, { hasShopButton: false });
        this.coinBar.container.resize = () => void 0;
        this.coinBar.container.position.set(200, 40);
        this.coinBar.container.alpha = 0;
        coinBarContainer.addChild(this.coinBar);

        const levelEntry = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted(state));

        rewardContainer.children.forEach((areaObject: any, index: number) => {
            const reward = rewardContainer.reward[index];
            const rewardText = reward.rewardText;
            const rewardValue = this.plinkoConfig.rewards[index].data.value * levelEntry;
            rewardText.getComponent(TextComponent).text = MathHelper.abbreviateNumber(rewardValue);
        });

        this.plinkoToken = GameObject.Instantiate(PlinkoToken, {
            x: 0,
            y: 0,
            pivot: new Point(0.5, 0.5),
        });

        const dropButtonInteractable = dropButton.getComponent(Interactable);
        dropButtonInteractable.update({
            tintOnDisable: true,
        });

        [dropButton, buyButton, buyExtraButton, claimButton, closeButton].forEach(button =>
            applyDefaultButtonAnimation(button, undefined, false, true)
        );

        // Set price text
        if (isShopSupported(state)) {
            const catalog = Game.sdk.getCatalog();
            const tokenProduct = catalog.products.find(e => e.productId === ProductId.PLINKO_TOKEN);
            const extraTokensProduct = catalog.products.find(e => e.productId === ProductId.PLINKO_TOKENS_2_99);
            if (!tokenProduct || !extraTokensProduct) {
                report('Failed to find plinko product', {
                    once: true,
                    sampleRate: 0.1,
                    tags: {
                        foundTokensProduct: !!tokenProduct,
                        foundExtraTokensProduct: !!extraTokensProduct,
                    },
                    fingerprint: 'PlinkoMiniGameHandler:OnCreate:FailedToFindProduct',
                });
                GameActions.loadWorld();
                return;
            }
            buyButtonText.getComponent(TextComponent).text = tokenProduct.price;
            buyExtraButtonText.getComponent(TextComponent).text = extraTokensProduct.price;
        } else {
            buyButtonText.getComponent(Renderable).visible = false;
            buyExtraButtonText.getComponent(Renderable).visible = false;
        }

        // Create overlay and add to layout
        const overlay: Overlay = GameObject.Instantiate(Overlay, {
            name: 'iap_info_overlay',
            preventInteraction: false,
            anchors: [0, 0, 0, 0],
            alpha: 0,
            pivot: [0, 0],
            color: [0, 0, 0, 225],
        });
        this.layout.root.addChild(overlay);

        // Save parents that will be added into overlay
        const buyParent = buyButton.parent;
        const buyExtraParent = buyButton.parent;
        const charParent = charSprite.parent;
        const closeParent = closeButton.parent;

        // Update the remaining tokens text
        this.remainingPurchasableTokensSelector = createSelector(
            (state: IGameState) => getRemainingPurchasablePlinkoTokens(state),
            (state: IGameState) => InventoryStateHelper.getInventoryCount(state, RewardType.PLINKO_TOKEN),
            (state: IGameState) => isShopSupported(state),
            (remainingPurchasableTokens: number, availableTokens: number, shopSupported) => {
                if (!dropTokenLeftText || dropTokenLeftText.destroyed) {
                    return;
                }

                const isFreeToken = remainingPurchasableTokens === this.plinkoConfig.purchasesAllowed && shopSupported;

                dropTokenLeftText.getComponent(TextComponent).text = Game.locale
                    .getText('xPlinkoTokenLeft')
                    .replace('[X]', availableTokens.toString());

                return remainingPurchasableTokens;
            }
        );

        // Update the button text
        this.dropButtonSelector = createSelector(
            this.isDropTokenAvailableSelector,
            this.isMinigameActiveSelector,
            isPlinkoRewardClaimable,
            (isDropTokenAvailable: boolean, miniGameActive: boolean, isRewardClaimable: boolean) => {
                if (!dropButton || dropButton.destroyed) {
                    return;
                }
                const enabled = isDropTokenAvailable && !miniGameActive;
                const showButton = (isDropTokenAvailable || miniGameActive) && !isRewardClaimable;

                dropButton.getComponent(Interactable).update({
                    enabled,
                    buttonMode: enabled,
                });

                // Show button if there is a token available or if the mini game is active, but only until the reward is claimable
                // StopAnimation(dropButton);
                StartAnimationClip(
                    new MainAnim(
                        dropButton,
                        {
                            alpha: Number(showButton),
                            duration: 250,
                        },
                        true
                    )
                );
            }
        );

        /**
         * Show buy button if:
         * no tokens are available
         * minigame is not active
         * reward is not claimable
         * reward animation is complete
         * iap is enabled
         */
        this.buyButtonSelector = createSelector(
            (state: IGameState) =>
                !this.isDropTokenAvailableSelector(state) &&
                !this.isMinigameActiveSelector(state) &&
                !isPlinkoRewardClaimable(state) &&
                isPlinkoRewardAnimationCompleted(state),
            state => isShopSupported(state),
            (shouldShowButton: boolean, shopSupported: boolean) => {
                if (!buyButton || buyButton.destroyed || !buyExtraButton || buyExtraButton.destroyed) {
                    return;
                }

                // Immediately return to world scene if Plinko reward animation is complete and IAP is not supported.
                if (shouldShowButton && !shopSupported) {
                    GameActions.loadWorld();
                    return;
                }

                iapInfo.getComponent(Renderable).alpha = Number(shouldShowButton);
                buyButton.getComponent(Renderable).alpha = Number(shouldShowButton);
                buyButton.getComponent(Interactable).update({
                    enabled: shouldShowButton,
                    buttonMode: shouldShowButton,
                });
                buyExtraButton.getComponent(Renderable).alpha = Number(shouldShowButton);
                buyExtraButton.getComponent(Interactable).update({
                    enabled: shouldShowButton,
                    buttonMode: shouldShowButton,
                });

                if (shouldShowButton) {
                    overlay.addChild(buyButton);
                    overlay.addChild(buyExtraButton);
                    overlay.addChild(charSprite);
                    overlay.addChild(closeButton);
                    StartAnimationClip(new OverlayFadeIn(overlay, { alpha: 1 }));
                } else {
                    StartAnimationClip(new OverlayFadeOut(overlay, { alpha: 1 }));
                    buyParent.addChild(buyButton);
                    buyExtraParent.addChild(buyExtraButton);
                    charParent.addChild(charSprite);
                    closeParent.addChild(closeButton);
                }
            }
        );
        this.claimButtonSelector = createSelector(isPlinkoRewardClaimable, (isRewardClaimable: boolean) => {
            if (!claimButton || claimButton.destroyed) {
                return;
            }
            claimButton.getComponent(Interactable).update({
                enabled: isRewardClaimable,
                buttonMode: isRewardClaimable,
            });
            StartAnimationClip(
                new MainAnim(
                    claimButton,
                    {
                        alpha: Number(isRewardClaimable),
                        duration: 250,
                    },
                    true
                )
            );
        });

        this.closeButtonSelector = createSelector(
            state =>
                !this.isDropTokenAvailableSelector(state) &&
                typeof state.plinkoState.rewardIndex !== 'number' &&
                isPlinkoRewardAnimationCompleted(state),
            (closeButtonActive: boolean) => {
                if (!closeButton || closeButton.destroyed) {
                    return;
                }

                closeButton.getComponent(Interactable).update({
                    enabled: closeButtonActive,
                    buttonMode: closeButtonActive,
                });

                StartAnimationClip(
                    new MainAnim(
                        closeButton,
                        {
                            alpha: Number(closeButtonActive),
                            duration: 250,
                        },
                        true
                    )
                );
            }
        );

        this.craneDropperSelector = createSelector(
            this.isDropTokenAvailableSelector,
            this.isMinigameActiveSelector,
            (isDropTokenAvailable: boolean, miniGameActive: boolean) => {
                // If token is available and the reward index is not null, then start the game
                if (miniGameActive) {
                    //
                } else if (isDropTokenAvailable) {
                    // `Reset` token position.
                    const dropperDragonBonesComponent = dropperArmature.getComponent(DragonbonesComponent);
                    dropperDragonBonesComponent.getSlotDisplay('Plinko_token').addChild(this.plinkoToken.container);
                    this.plinkoToken.container.position.set(0, 0);

                    dropperArmature.container.animation.reset();
                    setDropperAnimation(dropperArmature, 'idle');

                    // Reset rewards:
                    this.layout.rewardContainer.children.forEach(reward => {
                        StartAnimationClip(
                            new MainAnim(reward, { alpha: 1, y: 0, scaleX: 1, scaleY: 1, duration: 250 }, true)
                        );
                    });

                    // Reset armatures.
                    [this.layout.shadowArmature, this.layout.rewardArmature, this.layout.jackpotArmature].forEach(
                        armature => {
                            StartAnimationClip(new MainAnim(armature, { alpha: 0, duration: 250 }, true));
                        }
                    );
                }
            }
        );

        this.addEventListener(dropButton, GameObjectEvent.SELECTED, this.onDropClick);
        this.addEventListener(claimButton, GameObjectEvent.SELECTED, this.onClaimClick);
        this.addEventListener(buyButton, GameObjectEvent.SELECTED, this.onBuyClick);
        this.addEventListener(buyExtraButton, GameObjectEvent.SELECTED, this.onBuyExtraClick);
        this.addEventListener(closeButton, GameObjectEvent.SELECTED, this.onCloseClick);

        // Reset first layer animation in `onCreate` to prevent flickering when the idle animation is reset after first drop.
        dropperArmature.container.animation.reset();
        setDropperAnimation(dropperArmature, 'idle');

        this.handleStateChanged(state);
    }

    public handleStateChanged(state: IGameState): void {
        [
            this.dropButtonSelector,
            this.buyButtonSelector,
            this.claimButtonSelector,
            this.closeButtonSelector,
            this.craneDropperSelector,
        ].forEach(selector => selector(state));
        this.remainingPurchasableTokensSelector(state);
    }

    private onDropClick = (): void => {
        const { dropperArmature } = this.layout;

        const dropperState = dropperArmature.container.animation.getState('idle');
        const anim = getNextDropAnimation(dropperState.currentTime);

        // Give the player the reward and remove the token
        PlinkoActions.dropToken(anim.startIndex, anim.animation, this.plinkoConfig.rewards);
    };

    private onClaimClick = (): void => {
        const plinkoSystem = Game.scene.getActiveScene().getSystem(PlinkoSystem);
        const state = Game.store.getState();
        const reward = this.plinkoConfig.rewards[state.plinkoState.rewardIndex];
        const rewardData = scaleCoinRewardWithLevelCost(
            reward.data,
            LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState))
        );
        const levelData = state.levelState.completeData;

        // If `levelState` is not defined then do not reduce reward value. This is validated in case the minigame is
        // entered via a cheat or returning to the minigame after crash/quit.
        const levelCoinsToReduce = levelData ? levelData.coins + levelData.stackReward : 0;
        StartAnimationClip(
            new PlinkoRewardCoins(plinkoSystem.animator, {
                value: rewardData.value,
                previousValue: state.playerState.inventory.coins - rewardData.value - levelCoinsToReduce,
                target: this.coinBar,
            }).on(AnimationClipEvent.COMPLETE, () => {
                PlinkoActions.rewardAnimationComplete();
            })
        );
        PlinkoActions.claimReward();
    };

    private onCloseClick = (): void => {
        PlinkoActions.leavePlinkoScene();
    };

    private onBuyClick = (): void => {
        PlinkoActions.buyToken(this.tokenReward);
    };

    private onBuyExtraClick = (): void => {
        PlinkoActions.buyToken(this.extraTokenReward);
    };

    private addEventListener(
        object: GameObject,
        event: GameObjectEvent,
        listener: (...args: unknown[]) => any,
        context?: any,
        preventSpamDelay: number = 150
    ): void {
        let newListener = listener;
        if (preventSpamDelay) {
            let preventSpam = false;
            newListener = (...args: unknown[]) => {
                if (preventSpam) {
                    return;
                }
                preventSpam = true;
                Game.ticker.setTimeout(() => {
                    preventSpam = false;
                }, preventSpamDelay);
                listener(...args);
            };
        }
        object.on(event, newListener, context);

        this.cleanupManager.add(() => {
            object.off(event, newListener);
        });
    }
}

/**
 * Dropper animations should only play on layers 1 and 2. The idle animation plays on layer 1 and the drop animation
 * plays on layer 2. This way the drop animation can play additively on top of the idle animation for a natural look.
 * @param dropperArmature
 * @param animation
 */
export function setDropperAnimation(
    dropperArmature: GameObject<PlinkoMinigameLayout['dropperArmature']>,
    animation: 'idle' | 'drop'
) {
    const layer = animation === 'idle' ? 1 : 2;
    const playTimes = animation === 'idle' ? -1 : 1;

    const dropAnimationConfig = new AnimationConfig();
    dropAnimationConfig.copyFrom(dropperArmature.container.animation.animationConfig);
    dropAnimationConfig.animation = animation;
    dropAnimationConfig.fadeOutMode = AnimationFadeOutMode.SameLayer;
    dropAnimationConfig.playTimes = playTimes;
    dropAnimationConfig.layer = layer;
    dropAnimationConfig.fadeInTime = 0;
    dropAnimationConfig.group = animation;
    dropAnimationConfig.additive = true;
    dropAnimationConfig.resetToPose = false;
    dropAnimationConfig.weight = 1;

    dropperArmature.container.animation.playConfig(dropAnimationConfig);
}
